import React, { useMemo } from 'react'
import { Redirect } from 'react-router-dom'
import {
  Input,
  Container,
  Segment,
  Table,
  Grid,
  Loader,
  Icon,
  Label,
  Dropdown
} from 'semantic-ui-react'


import titleCase from 'title-case'
import './Applicants.css'
import PaginationTable from '../../components/PaginationTable/PaginationTable'
import constants from '../../constants'
// import ApplicantsReadCSV from './ApplicantsReadCSV'
// import ApplicantsAdd from './ApplicantsAdd'

// Regular imports for React 16.8 compatibility
import ApplicantsEdit from './ApplicantsEdit'
import ApplicantsDelete from './ApplicantsDelete'
import ApplicantsView from './ApplicantsView'
import ApplicantsSendEmail from './ApplicantsSendEmail'
import ApplicantsSendAAEmail from './ApplicantsSendAAEmail'
import ApplicantsExport from './ApplicantsExport'

const optionsGradeLevel = constants.breakdownGradeLevel;

const changeCase = {
    titleCase: titleCase
}
const currencyFormatter = new Intl.NumberFormat();

const ApplicantsPV = (props) => {
  if (props.auth) {
    if (props.userType === 'admin' || props.userType === 'principal' || props.userType === 'registrar' || props.userType === 'cashier' || props.userType === 'staff') {
      const { activePage, itemPerPage, column, direction, data, searchKey, debouncedSearchKey, fields, loaded, gradeLevel } = props;

      // Memoize filtered data with debounced search - ultra smooth performance!
      const currentData = useMemo(() => {
        // Use debouncedSearchKey for filtering, but searchKey for UI display
        let filtered = data.filter(props.isSearched(debouncedSearchKey || ''));
        if (gradeLevel !== '') {
          filtered = filtered.filter(applicant => applicant.gradeLevel === gradeLevel);
        }
        return filtered;
      }, [data, debouncedSearchKey, gradeLevel, props.isSearched]);

      // Memoize dropdown options
      const optionsSchoolYears = useMemo(() => {
        if (!props.schoolYears) return [];
        return props.schoolYears.map((schoolYear) => ({
          key: schoolYear.schoolYearId, 
          value: schoolYear.schoolYearId, 
          text: schoolYear.name
        }));
      }, [props.schoolYears]);

      // Simple pagination calculations
      const paginationData = useMemo(() => {
        const indexOfLast = activePage * itemPerPage;
        const indexOfFirst = indexOfLast - itemPerPage;
        const rowLength = currentData.length;
        const paginatedData = currentData.slice(indexOfFirst, indexOfLast);
        const totalPages = Math.ceil(rowLength / itemPerPage);
        return { paginatedData, totalPages, rowLength };
      }, [currentData, activePage, itemPerPage]);

      const { paginatedData, totalPages, rowLength } = paginationData;


      // Memoize table row rendering - massive performance gain!
      const applicantTable = useMemo(() => {
        return paginatedData.map((applicant, key) => {
          let statusColor;
          if(applicant.clearToEnroll === 'Temp') {
            statusColor = 'yellow'
          } else if (applicant.clearToEnroll === 'Not Ok') {
            statusColor = 'red'
          } else if (applicant.clearToEnroll === 'Ok') {
            statusColor = 'green'
          } else {
            statusColor = 'grey'
          }
          return (
            <Table.Row key={key} className='applicants-table-row'>
              <Table.Cell collapsing>{applicant.appNumber}</Table.Cell>
              <Table.Cell collapsing>
                <Label color={applicant.applicationType === 'new' ? 'green' : 'yellow'} horizontal>
                  {changeCase.titleCase(applicant.applicationType)}
                </Label>
              </Table.Cell>
              <Table.Cell>{applicant.lastName}</Table.Cell>
              <Table.Cell>{applicant.firstName}</Table.Cell>
              <Table.Cell>{applicant.email}</Table.Cell>
              <Table.Cell collapsing>{applicant.paymentSchedule}</Table.Cell>
              <Table.Cell collapsing textAlign='center'>{applicant.isPassed ? <Icon name='check' color='green'/> : applicant.applicationType === 'new' ? <Icon name='close' color='red' /> : <Icon name='minus' /> }</Table.Cell>
              <Table.Cell collapsing textAlign='right'>{
                applicant.toPayUponEnrollment === '--' ? '--' : currencyFormatter.format(applicant.toPayUponEnrollment)
              }
              </Table.Cell>
              <Table.Cell textAlign='center' collapsing>
                <Label color={statusColor} horizontal>
                  {changeCase.titleCase(applicant.clearToEnroll)}
                </Label>
              </Table.Cell>
              <Table.Cell collapsing textAlign='center'>{applicant.isPaid ? <Icon name='check' color='green'/> : <Icon name='close' color='red' />}</Table.Cell>
              <Table.Cell collapsing>
                <ApplicantsView
                  applicant={applicant}
                />
                <ApplicantsSendAAEmail
                  applicant={applicant}
                  getApplicants={props.getApplicants}
                />
                <ApplicantsSendEmail
                  applicant={applicant}
                  getApplicants={props.getApplicants}
                />
                <ApplicantsEdit
                  applicant={applicant}
                  schoolYears={props.schoolYears}
                  students={props.students}
                  paymentFile={props.paymentFile}
                  enrollmentFilename={props.enrollmentFile}
                  gradeFilename={props.gradeFilename}
                  handleUpdate={props.handleUpdate}
                  handleSaveAsStudent={props.handleSaveAsStudent}
                  handleSaveAsOldStudent={props.handleSaveAsOldStudent}
                  handleAddStudentToSY={props.handleAddStudentToSY}
                  handleStudentPayment={props.handleStudentPayment}
                  getOldStudent={props.getOldStudent}
                  handleUploadFileChange={props.handleUploadFileChange}
                  handleUploadGradeFileChange={props.handleUploadGradeFileChange}
                  handleUploadEnrollmentFileChange={props.handleUploadEnrollmentFileChange}
                />

                <ApplicantsDelete
                  applicantId={applicant.applicantId}
                  applicantName={`${applicant.firstName} ${applicant.lastName}`}
                  handleDelete={props.handleDelete}
                />
              </Table.Cell>
            </Table.Row>
          );
        });
      }, [paginatedData, props.getApplicants, props.schoolYears, props.students, props.paymentFile, props.enrollmentFile, props.gradeFilename, props.handleUpdate, props.handleSaveAsStudent, props.handleSaveAsOldStudent, props.handleAddStudentToSY, props.handleStudentPayment, props.getOldStudent, props.handleUploadFileChange, props.handleUploadGradeFileChange, props.handleUploadEnrollmentFileChange, props.handleDelete]);

      // Memoize table headers
      const headers = useMemo(() => {
        return fields.map((field,index) => {
        if (field === 'UE') {
          return (
            <Table.HeaderCell
              key={index}
              sorted={column === field ? direction : null}
              onClick={() => props.handleSort(field)}
              textAlign='center'
            >
              UE
            </Table.HeaderCell>
          )
        } else if (field === 'AA') {
          return (
            <Table.HeaderCell
              key={index}
              sorted={column === field ? direction : null}
              onClick={() => props.handleSort(field)}
              textAlign='center'
            >
              AA
            </Table.HeaderCell>
          )
        } else {
          return (
            <Table.HeaderCell
              key={index}
              sorted={column === field ? direction : null}
              onClick={() => props.handleSort(field)}
              textAlign='center'
            >
              {changeCase.titleCase(field)}
            </Table.HeaderCell>
          )
        }
        });
      }, [fields, column, direction, props.handleSort]);

      return (
        <div>
          <Container>
            <Segment>
              <Grid>
                <Grid.Row>
                  <Grid.Column floated='left' width={3}>
                    <Grid.Column>
                      <Input
                        icon='search'
                        value={searchKey}
                        onChange={props.onSearchChange}
                        placeholder='Search Applicants'
                      />
                    </Grid.Column>
                  </Grid.Column>
                  <Grid.Column width={3}>
                    <Dropdown
                      selection
                      name='schoolYearId'
                      options={optionsSchoolYears}
                      onChange={props.handleChangeSchoolYear}
                      value={props.schoolYearId}
                    />
                  </Grid.Column>
                  <Grid.Column floated='right' width={4}>

                  Total Number of Applicants: {`${data.length}`}<br />
                  Total Number of Filtered Applicants: {`${rowLength}`}
                  </Grid.Column>
                  <Grid.Column floated='right' width={3}>

                    <Dropdown
                      selection
                      search
                      clearable
                      placeholder='Grade Level'
                      options={optionsGradeLevel}
                      onChange={props.handleChange}
                      name='gradeLevel'
                      value={props.gradeLevel}
                    />

                  </Grid.Column>
                  <Grid.Column width={3}>
                    {
                      rowLength > 0 ?
                      <ApplicantsExport data={currentData} gradeLevel={gradeLevel}/> :
                      null
                    }
                  </Grid.Column>
                </Grid.Row>

              </Grid>
              {
                loaded ?
                <div className='table-applicants'>
                  {
                    data.length > 0 ?
                    <Table celled selectable sortable striped compact size='small'>
                      <Table.Header>
                        <Table.Row>
                          {headers}
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>{applicantTable}</Table.Body>
                        {
                          data.length > itemPerPage ?
                          <PaginationTable
                            onPageChange={props.handlePaginationChange}
                            totalPages={totalPages}
                            activePage={activePage}
                            fields={fields}
                          >
                            {props.children}
                          </PaginationTable>
                          :
                          null
                        }
                    </Table>
                    :
                    <Segment>No applicant records found.</Segment>
                  }
                </div>

                :
                <Loader active inline='centered' />
              }

            </Segment>
          </Container>
        </div>
      )
    } else {
      return <Segment>You are not authorized to be in this page.</Segment>
    }

  } else {
    return <Redirect to='/' />
  }

}

// Simple React.memo for React 16.8 compatibility
export default React.memo(ApplicantsPV);
